<template>
  <footer class="relative overflow-hidden w-full font-sans text-slate-600 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-950 transition-colors duration-300">
    
    <div aria-hidden="true" class="pointer-events-none absolute inset-0 z-0">
      <div class="absolute -top-40 -left-40 h-96 w-96 rounded-full 
                  bg-blue-100/50 blur-3xl mix-blend-multiply 
                  dark:bg-blue-900/20 dark:mix-blend-normal
                  animate-blob"></div>
      <div class="absolute -bottom-40 -right-40 h-96 w-96 rounded-full 
                  bg-indigo-100/50 blur-3xl mix-blend-multiply 
                  dark:bg-indigo-900/20 dark:mix-blend-normal
                  animate-blob animation-delay-2000"></div>
    </div>

    <div class="relative z-10">
      
      <transition name="fade-slide">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" v-if="isEditMode">
          <div class="flex justify-end">
            <button 
              @click="emitEdit" 
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold 
                     text-slate-700 bg-white border border-slate-200 
                     dark:text-slate-200 dark:bg-slate-800 dark:border-slate-700
                     rounded-full hover:bg-slate-800 hover:text-white dark:hover:bg-slate-700 hover:border-transparent 
                     shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer active:scale-95"
            >
              <i class="fas fa-pen-to-square"></i> 
              {{ currentLang === 'en' ? 'Edit footer' : 'Éditer le footer' }}
            </button>
          </div>
        </div>
      </transition>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          
          <div 
            :class="['lg:col-span-5 flex flex-col h-full bg-white/70 dark:bg-slate-900/60 backdrop-blur-md rounded-3xl p-8 ring-1 ring-slate-200 dark:ring-slate-800 shadow-sm transition-all duration-700 ease-out hover:shadow-lg hover:ring-blue-200/60 dark:hover:ring-slate-700 hover:-translate-y-1', isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12']"
          >
            <div class="flex-1">
              <h3 class="text-2xl font-black tracking-tight text-slate-800 dark:text-slate-100 break-words">
                {{ main?.titre || 'H&S Conseil' }}
              </h3>
              <p class="mt-4 text-base leading-relaxed text-pretty text-slate-600 dark:text-slate-400">
                {{ main?.description || (currentLang === 'en' ? 'Your expert partner in digital transformation.' : 'Votre partenaire expert en transformation numérique.') }}
              </p>
            </div>

            <div class="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800">
              <p class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase mb-4">
                {{ currentLang === 'en' ? 'Follow us' : 'Suivez-nous' }}
              </p>
              <div class="flex flex-wrap gap-3">
                <a 
                  v-for="s in socialsNormalized" 
                  :key="s.id" 
                  :href="s.href" 
                  target="_blank"
                  class="group flex items-center justify-center h-10 w-10 rounded-full 
                         bg-white border border-slate-200 text-slate-500 
                         dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400
                         hover:bg-slate-900 hover:text-white dark:hover:bg-slate-700 dark:hover:text-slate-200
                         hover:scale-110 active:scale-90 transition-all duration-300 shadow-sm cursor-pointer"
                >
                  <i :class="s.iconClass" class="text-lg transition-transform group-hover:rotate-6"></i>
                </a>
              </div>
            </div>
          </div>

          <nav 
            :class="['lg:col-span-3 flex flex-col h-full bg-white/70 dark:bg-slate-900/60 backdrop-blur-md rounded-3xl p-8 ring-1 ring-slate-200 dark:ring-slate-800 shadow-sm transition-all duration-700 ease-out delay-150 hover:shadow-lg hover:ring-blue-200/60 dark:hover:ring-slate-700 hover:-translate-y-1', isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12']"
          >
            <h4 class="text-sm font-bold tracking-widest text-slate-800 dark:text-slate-200 uppercase mb-6 border-b border-slate-100 dark:border-slate-800 pb-2">
              {{ currentLang === 'en' ? 'Explore' : 'Explorer' }}
            </h4>
            
            <ul class="space-y-3 flex-1">
              <li v-for="l in linksNormalized" :key="l.id">
                <component 
                  :is="l.internal ? 'router-link' : 'a'" 
                  :to="l.internal ? l.href : undefined"
                  :href="!l.internal ? l.href : undefined"
                  :target="!l.internal ? '_blank' : undefined"
                  class="group flex items-center gap-3 p-2 -mx-2 rounded-lg 
                         hover:bg-white dark:hover:bg-slate-800 
                         transition-all duration-200 cursor-pointer w-full hover:pl-3"
                >
                  <span class="flex shrink-0 items-center justify-center h-6 w-6 rounded 
                               bg-blue-50 text-blue-600 
                               dark:bg-slate-800 dark:text-blue-400
                               group-hover:bg-blue-600 group-hover:text-white dark:group-hover:bg-blue-500 
                               transition-colors duration-300">
                     <i class="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                  </span>
                  <span class="text-sm font-medium group-hover:text-slate-900 dark:group-hover:text-slate-100 break-words transition-colors">
                    {{ l.label }}
                  </span>
                </component>
              </li>
            </ul>
          </nav>

          <div 
            :class="['lg:col-span-4 flex flex-col h-full bg-white/70 dark:bg-slate-900/60 backdrop-blur-md rounded-3xl p-8 ring-1 ring-slate-200 dark:ring-slate-800 shadow-sm transition-all duration-700 ease-out delay-300 hover:shadow-lg hover:ring-blue-200/60 dark:hover:ring-slate-700 hover:-translate-y-1', isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12']"
          >
            <h4 class="text-sm font-bold tracking-widest text-slate-800 dark:text-slate-200 uppercase mb-6 border-b border-slate-100 dark:border-slate-800 pb-2">
              {{ currentLang === 'en' ? 'Contact' : 'Contact' }}
            </h4>
            
            <div class="flex flex-col gap-3 flex-1">
              <a 
                v-for="c in contactWithLinks" 
                :key="c.id" 
                :href="c.link"
                :target="c.type === 'address' ? '_blank' : undefined"
                class="group flex items-start gap-4 p-3 rounded-2xl 
                       bg-white/50 border border-slate-100 
                       dark:bg-slate-800/40 dark:border-slate-700
                       hover:bg-white hover:border-blue-200 dark:hover:bg-slate-800 dark:hover:border-slate-600
                       hover:shadow-sm transition-all duration-300 cursor-pointer w-full hover:scale-[1.02]"
              >
                <div class="shrink-0 h-10 w-10 rounded-lg flex items-center justify-center 
                            bg-white text-blue-600 shadow-sm ring-1 ring-slate-100
                            dark:bg-slate-800 dark:text-blue-400 dark:ring-slate-700
                            group-hover:bg-blue-600 group-hover:text-white dark:group-hover:bg-blue-500
                            group-hover:scale-105 transition-all duration-300">
                  <i :class="c.iconClass" class="text-base"></i>
                </div>
                
                <div class="flex-1 min-w-0 pt-0.5">
                  <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase leading-none mb-1 transition-colors group-hover:text-blue-400">
                    {{ c.labelDisplay }}
                  </p>
                  <p :class="['text-sm font-semibold text-slate-800 dark:text-slate-200 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors leading-tight', c.type === 'email' ? 'break-all' : 'break-words']">
                    {{ c.value || (currentLang === 'en' ? 'Information' : 'Information') }}
                  </p>
                </div>
              </a>
            </div>
          </div>

        </div>

        <div 
          :class="['mt-12 pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 transition-opacity duration-1000 delay-700', isVisible ? 'opacity-100' : 'opacity-0']"
        >
          <div class="flex flex-col md:flex-row items-center gap-3 md:gap-4 text-center md:text-left">
            
            <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-500 font-medium">
              {{ note?.description || (currentLang === 'en' ? '© 2025 H&S Conseil. All rights reserved.' : '© 2025 H&S Conseil. Tous droits réservés.') }}
            </p>
            
            <span class="hidden md:inline text-slate-300 dark:text-slate-700">|</span>

            <router-link 
              :to="{ name: currentLang === 'en' ? 'privacy-en' : 'privacy-fr', params: { langue: currentLang } }"
              class="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
            >
              {{ currentLang === 'en' ? 'Privacy Policy' : 'Politique de confidentialité' }}
            </router-link>

            <span class="hidden md:inline text-slate-300 dark:text-slate-700">|</span>

            <button 
              type="button" 
              @click="openCookieSettings" 
              class="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors underline decoration-slate-300/50 underline-offset-4 cursor-pointer"
            >
              {{ currentLang === 'en' ? 'Manage Cookies' : 'Gérer les cookies' }}
            </button>
          </div>

          <div class="order-1 md:order-2 flex items-center gap-2 opacity-60 grayscale hover:grayscale-0 transition-all">
             <i class="fa-solid fa-shield-halved text-xs"></i>
             <span class="text-xs">
               {{ currentLang === 'en' ? 'Secure Site' : 'Site sécurisé' }}
             </span>
          </div>
        </div>

      </div>
    </div>
  </footer>
</template>

<script>
import http from "@/api/http";
// IMPORT OBLIGATOIRE POUR LA GESTION DES COOKIES
import * as CookieConsent from 'vanilla-cookieconsent';

export default {
  name: "FooterComponent",
  props: { isEditMode: { type: Boolean, default: false } },
  data() {
    return {
      main: null,
      links: [],
      socials: [],
      contact: [],
      note: null,
      loading: false,
      isVisible: false, 
    };
  },
  computed: {
    currentLang() {
      const fromStore = this.$store?.state?.langue;
      const fromRoute = this.$route?.params?.langue;
      const lang = fromStore || fromRoute || "fr";
      return ["fr", "en"].includes(lang) ? lang : "fr";
    },
    linksNormalized() {
        return (this.links || []).map((l) => {
            const raw = (l.description || "").trim();
            const isExternal = /^https?:\/\//i.test(raw);
            const href = isExternal ? raw : this.localizePath(raw);
            const defaultLabel = this.currentLang === 'en' ? "Link" : "Lien";
            return { id: l.id, label: l.titre || defaultLabel, href, internal: !isExternal };
        });
    },
    socialsNormalized() {
        return (this.socials || []).map((s) => {
            const key = (s.titre || "").toLowerCase().trim();
            return { id: s.id, href: s.description || "#", iconClass: this.mapSocialIcon(key) };
        });
    },
    contactWithLinks() {
      return (this.contact || []).map((c) => {
        const type = (c.titre || "").toLowerCase().trim();
        const value = c.description || "";
        
        let link = "#";
        let labelDisplay = this.currentLang === 'en' ? "Info" : "Info";

        if (type === 'email') {
            link = `mailto:${value}`;
            labelDisplay = "Email"; 
        } else if (type === 'phone') {
            const cleanPhone = value.replace(/[^\d+]/g, ''); 
            link = `tel:${cleanPhone}`;
            labelDisplay = this.currentLang === 'en' ? "Phone" : "Téléphone";
        } else if (type === 'address') {
            link = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`;
            labelDisplay = this.currentLang === 'en' ? "Address" : "Adresse";
        }

        return { 
          id: c.id, 
          type, 
          value, 
          link,
          labelDisplay,
          iconClass: this.mapContactIcon(type) 
        };
      });
    }
  },
  watch: {
    currentLang() { this.fetchFooter(); },
  },
  mounted() {
    this.fetchFooter();
    setTimeout(() => {
      this.isVisible = true;
    }, 100);
  },
  methods: {
    // ------------------------------------------------------------------
    // OUVRE LA MODALE DE PREFERENCES COOKIES
    // ------------------------------------------------------------------
    openCookieSettings() {
      CookieConsent.showPreferences();
    },

    async fetchFooter() {
        try {
            this.loading = true;
            const { data } = await http.get("footer-content", { params: { langue: this.currentLang } });
            this.main = data.main;
            this.links = data.links;
            this.socials = data.socials;
            this.contact = data.contact;
            this.note = data.note;
        } catch (e) {
            console.error("Erreur footer:", e);
        } finally {
            this.loading = false;
        }
    },
    localizePath(raw) {
        let p = raw.startsWith("/") ? raw : `/${raw}`;
        if (/^\/(fr|en)\//i.test(p)) return p.replace(/^\/(fr|en)\//i, `/${this.currentLang}/`);
        return `/${this.currentLang}${p === '/' ? '' : p}`;
    },
    mapSocialIcon(key) {
        const map = { 
            linkedin: 'fa-brands fa-linkedin-in', 
            facebook: 'fa-brands fa-facebook-f', 
            instagram: 'fa-brands fa-instagram', 
            twitter: 'fa-brands fa-x-twitter', 
            x: 'fa-brands fa-x-twitter',
            youtube: 'fa-brands fa-youtube',
            tiktok: 'fa-brands fa-tiktok',
            github: 'fa-brands fa-github'
        };
        return map[key] || 'fa-solid fa-globe';
    },
    mapContactIcon(type) {
        const map = { 
            address: 'fa-solid fa-location-dot', 
            email: 'fa-solid fa-envelope', 
            phone: 'fa-solid fa-phone' 
        };
        return map[type] || 'fa-solid fa-info';
    },
    emitEdit() {
      this.$emit("edit", {
        main: this.main,
        links: this.links,
        socials: this.socials,
        contact: this.contact,
        note: this.note,
        langue: this.currentLang,
      });
    },
  },
};
</script>

<style scoped>
/* Animation Blob (Fond qui respire) */
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}
.animate-blob {
  animation: blob 7s infinite;
}
.animation-delay-2000 {
  animation-delay: 2s;
}

/* Transitions Vue pour le bouton Admin */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

footer {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>